import React, { useContext, useEffect } from "react";
import { collection, doc, onSnapshot } from "firebase/firestore";
import { Context } from "../Cache";
import { FirebaseContext } from "../Firebase";
import AuthUserContext from "../Session/authContext";
import api from "../../utils/api";
import { getSubdomain } from "../../utils/api";

const AppBootstrap = ({ children }) => {
  const { state, actions } = useContext(Context);
  const firebase = useContext(FirebaseContext);
  const authUser = useContext(AuthUserContext);
  const widgetsKey = (state.config?.appearance?.widgets || []).join("|");

  useEffect(() => {
    let unsubscribeAppearance = null;
    let unsubscribeIncident = null;
    let unsubscribeForm = null;

    if (!firebase) {
      return;
    }

    const subdomain = getSubdomain();
    if (!subdomain) {
      return;
    }

    const teamRef = firebase.getTeamRef(subdomain);
    const configRef = collection(teamRef, "config");

    const configState = {
      appearance: null,
      incident: null,
      form: null,
      customPermissions: null,
      timezone: null,
    };

    const maybeDispatch = () => {
      if (configState.appearance && configState.incident && configState.form) {
        const nextConfig = {
          appearance: configState.appearance,
          incident: configState.incident,
          form: configState.form,
        };
        const timezone =
          configState.timezone || configState.appearance?.timezone;
        if (timezone) {
          nextConfig.timezone = timezone;
        }
        if (configState.customPermissions) {
          nextConfig.customPermissions = configState.customPermissions;
        }
        actions.setConfig(nextConfig);
      }
    };

    const fetchCustomPermissions = async () => {
      try {
        const result = await api.get(`/configGet`);
        const apiConfig = result?.data?.config || {};
        const customPermissions = apiConfig.customPermissions;
        if (customPermissions) {
          configState.customPermissions = customPermissions;
        }
        if (apiConfig.timezone) {
          configState.timezone = apiConfig.timezone;
        }
        maybeDispatch();
      } catch (e) {
        // Non-blocking: Firestore config still drives the UI.
      }
    };

    fetchCustomPermissions();

    unsubscribeAppearance = onSnapshot(
      doc(configRef, "appearance"),
      (snap) => {
        configState.appearance = snap.data() || {};
        maybeDispatch();
      },
    );

    unsubscribeIncident = onSnapshot(
      doc(configRef, "incident"),
      (snap) => {
        configState.incident = snap.data() || {};
        maybeDispatch();
      },
    );

    unsubscribeForm = onSnapshot(
      doc(configRef, "form"),
      (snap) => {
        configState.form = snap.data() || {};
        maybeDispatch();
      },
    );

    return () => {
      if (unsubscribeAppearance) {
        unsubscribeAppearance();
      }
      if (unsubscribeIncident) {
        unsubscribeIncident();
      }
      if (unsubscribeForm) {
        unsubscribeForm();
      }
    };
  }, [actions, firebase]);

  useEffect(() => {
    let unsubscribeMembers = null;
    let unsubscribeTags = null;

    if (!firebase || !authUser || !authUser.whitelisted) {
      return;
    }

    const subdomain = getSubdomain();
    if (!subdomain) {
      return;
    }

    const teamRef = firebase.getTeamRef(subdomain);
    const usersRef = collection(teamRef, "users");
    const tagsRef = collection(teamRef, "tags");

    unsubscribeMembers = onSnapshot(
      usersRef,
      (snap) => {
        const users = [];
        snap.forEach((docSnap) => {
          const user = docSnap.data();
          user.id = docSnap.id;
          users.push(user);
        });
        actions.setMembers(users);
      },
      (err) => {
        actions.setMembers([]);
      }
    );

    unsubscribeTags = onSnapshot(
      tagsRef,
      (snap) => {
        const tags = [];
        snap.forEach((docSnap) => {
          tags.push({
            id: docSnap.id,
            ...docSnap.data(),
          });
        });
        tags.sort((a, b) => (a.name || "").localeCompare(b.name || ""));
        actions.setTags(tags);
      },
      () => {
        actions.setTags([]);
      }
    );

    return () => {
      if (unsubscribeMembers) {
        unsubscribeMembers();
      }
      if (unsubscribeTags) {
        unsubscribeTags();
      }
    };
  }, [actions, firebase, authUser, widgetsKey]);

  return children;
};

export default AppBootstrap;
